import React, { createContext, type JSX, type ReactNode, useContext, useRef } from 'react';

interface ISessionCacheContext {
  getValue: <T>(key: string, fetcher: () => T | Promise<T>) => T | Promise<T>;
}

export const SessionCacheContext = createContext<ISessionCacheContext | undefined>(undefined);

interface ISessionCacheProviderProps {
  children: ReactNode;
}

export function SessionCacheProvider({ children }: ISessionCacheProviderProps): JSX.Element {
  const cache = useRef<Map<string, unknown>>(new Map());

  const getValue = <T,>(key: string, fetcher: () => T | Promise<T>): T | Promise<T> => {
    if (cache.current.has(key)) {
      return cache.current.get(key) as T;
    }

    const result = fetcher();

    if (result instanceof Promise) {
      return result.then((value) => {
        cache.current.set(key, value);
        return value;
      });
    }

    cache.current.set(key, result);
    return result;
  };

  return (
    <SessionCacheContext.Provider value={{ getValue }}>{children}</SessionCacheContext.Provider>
  );
}

export function useSessionCache(): ISessionCacheContext {
  const context = useContext(SessionCacheContext);
  if (!context) {
    throw new Error('useSessionCache must be used within a SessionCacheProvider');
  }
  return context;
}
